import SplitControl from '@/components/business/SplitControl'
import { MenuItemProps } from '@/resources/types/menu/types'
import { HexaV2NavItem } from '@/components/hexa-v2'
import { SidebarMenuItems } from '@/resources/enums'
import { ScreenName } from '@/analytics/generated/segment'
import { useSeller } from '@/contexts/seller'
import useFinancialReportTitleExperiment, {
  FINANCIAL_REPORT_TITLE_EXPERIMENT_NAME,
  FINANCIAL_REPORT_TITLE_EXPERIMENT_OWNER,
} from '@/hooks/useFinancialReportTitleExperiment'
import useManualExperimentExposure from '@/hooks/useManualExperimentExposure'

const getManualExperimentExposureDeduplicationKey = ({
  experimentName,
  pocId,
  variantName,
}: {
  experimentName: string
  pocId?: number
  variantName: string
}) => {
  if (!pocId) return undefined

  return `manual_experiment_exposure:${experimentName}:${pocId}:${variantName}`
}

const NavItem = ({ menuItem, onClick, isItemActive }: MenuItemProps) => (
  <HexaV2NavItem
    active={isItemActive}
    key={menuItem.id}
    icon={menuItem.icon}
    label={menuItem.text}
    onHxChangeActiveNavItem={() => onClick?.(menuItem)}
  />
)

const FinancialReportTitleExperimentMenuItem = ({
  menuItem,
  onClick,
  isItemActive,
}: MenuItemProps) => {
  const titleExperiment = useFinancialReportTitleExperiment()
  const { sellerData } = useSeller()
  const exposureDeduplicationKey = getManualExperimentExposureDeduplicationKey({
    experimentName: FINANCIAL_REPORT_TITLE_EXPERIMENT_NAME,
    pocId: sellerData?.seller?.id,
    variantName: titleExperiment.variantName,
  })

  const menuItemWithExperimentTitle = {
    ...menuItem,
    text: titleExperiment.getTitle(menuItem.text),
  }

  useManualExperimentExposure({
    deduplicationKey: exposureDeduplicationKey,
    experimentName: FINANCIAL_REPORT_TITLE_EXPERIMENT_NAME,
    isReady: titleExperiment.isReady && Boolean(exposureDeduplicationKey),
    owner: FINANCIAL_REPORT_TITLE_EXPERIMENT_OWNER,
    ownerSquad: FINANCIAL_REPORT_TITLE_EXPERIMENT_OWNER,
    screenName: ScreenName.FinancialReport,
    variantId: titleExperiment.variantId,
    variantName: titleExperiment.variantName,
  })

  return (
    <NavItem
      menuItem={menuItemWithExperimentTitle}
      onClick={onClick}
      isItemActive={isItemActive}
    />
  )
}

const SplitControlledMenuItem = ({
  menuItem,
  onClick,
  isItemActive,
}: MenuItemProps) => {
  if (menuItem.id === SidebarMenuItems.FINANCIAL_REPORT) {
    return (
      <FinancialReportTitleExperimentMenuItem
        menuItem={menuItem}
        onClick={onClick}
        isItemActive={isItemActive}
      />
    )
  }

  return (
    <>
      {menuItem.featureFlag && menuItem.splitName ? (
        <SplitControl splitName={menuItem.splitName} treatmentOff={null}>
          <NavItem
            menuItem={menuItem}
            onClick={onClick}
            isItemActive={isItemActive}
          />
        </SplitControl>
      ) : (
        <NavItem
          menuItem={menuItem}
          onClick={onClick}
          isItemActive={isItemActive}
        />
      )}
    </>
  )
}

export default SplitControlledMenuItem
